import { Button } from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { useI18n } from '../../i18n';
import './AddToCalendar.css';

/**
 * "Add to calendar" — a small, self-contained block with two actions:
 *   • Google Calendar  → opens the render link with the event pre-filled
 *   • Outlook / .ics   → downloads a standard .ics file (works with Outlook, Apple, etc.)
 *
 * Everything is generated CLIENT-SIDE from props, so it works both inside the app (Step6 /
 * appointment view) and on the public /add-to-calendar landing page reached from the SMS link.
 * No PII is required — the event uses the department/service as its title.
 *
 * Props:
 *   title            {string}  event title (e.g. "תור: רפואת שיניים")
 *   start            {Date|string}  start time — a Date, or a local ISO string "YYYY-MM-DDTHH:mm"
 *   durationMinutes  {number}  slot length (default 15)
 *   location         {string}  address (optional)
 *   details          {string}  extra notes (optional)
 */
function pad(n) { return String(n).padStart(2, '0'); }

// JS Date → "YYYYMMDDTHHMMSSZ" (UTC) — the format Google Calendar and .ics both expect.
function toUtcStamp(d) {
  return d.getUTCFullYear() + pad(d.getUTCMonth() + 1) + pad(d.getUTCDate())
    + 'T' + pad(d.getUTCHours()) + pad(d.getUTCMinutes()) + pad(d.getUTCSeconds()) + 'Z';
}

// escape the ICS text fields (RFC 5545): backslash, comma, semicolon, and newlines.
function icsEscape(s) {
  return String(s == null ? '' : s)
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r\n|\r|\n/g, '\\n');   // CRLF, lone CR, and lone LF all → escaped \n
}

function normalizeStart(start) {
  const d = start instanceof Date ? start : new Date(start);
  return isNaN(d.getTime()) ? null : d;
}

export default function AddToCalendar({ title, start, durationMinutes = 15, location = '', details = '' }) {
  // ⚠️ ההוק קודם ל-return המוקדם.
  const { t, dir } = useI18n();
  const startDate = normalizeStart(start);
  if (!startDate) return null;

  const dur = Math.max(1, parseInt(durationMinutes, 10) || 15);
  const endDate = new Date(startDate.getTime() + dur * 60000);
  const evTitle = title || t('addToCalendar.defaultEventTitle');

  const googleUrl = (() => {
    const u = new URL('https://calendar.google.com/calendar/render');
    u.searchParams.set('action', 'TEMPLATE');
    u.searchParams.set('text', evTitle);
    u.searchParams.set('dates', `${toUtcStamp(startDate)}/${toUtcStamp(endDate)}`);
    if (details) u.searchParams.set('details', details);
    if (location) u.searchParams.set('location', location);
    return u.toString();
  })();

  const openGoogle = () => window.open(googleUrl, '_blank', 'noopener,noreferrer');

  const downloadIcs = () => {
    const uid = `ans-${startDate.getTime()}-${Math.floor(Math.random() * 1e6)}@anstc`;
    const now = toUtcStamp(new Date());
    const lines = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//ANS Tech//Appointments//HE',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTAMP:${now}`,
      `DTSTART:${toUtcStamp(startDate)}`,
      `DTEND:${toUtcStamp(endDate)}`,
      `SUMMARY:${icsEscape(evTitle)}`,
      location ? `LOCATION:${icsEscape(location)}` : null,
      details ? `DESCRIPTION:${icsEscape(details)}` : null,
      'END:VEVENT',
      'END:VCALENDAR',
    ].filter(Boolean);
    const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'appointment.ics';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div className="ans-atc" dir={dir}>
      <div className="ans-atc-title"><EventAvailableIcon fontSize="small" /> {t('addToCalendar.title')}</div>
      <div className="ans-atc-btns">
        <Button variant="outlined" className="ans-atc-btn ans-atc-google" onClick={openGoogle}>
          {t('addToCalendar.google')}
        </Button>
        <Button variant="outlined" className="ans-atc-btn ans-atc-ics" onClick={downloadIcs}>
          {t('addToCalendar.outlook')}
        </Button>
      </div>
    </div>
  );
}
